import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Col } from "react-bootstrap";
import { IoCloseSharp } from "react-icons/io5";
import DatePicker from "react-datepicker";
import { pdf } from "@react-pdf/renderer";
import RelatorioPDF from "./RelatorioPDF";

import {
  AppliedFiltersWrapper,
  Container,
  ContainerButton,
  FilterItem,
  FilterKey,
  FiltersContainer,
  FilterTitle,
  FilterValue,
} from "./styles";
import {
  BlueTitle,
  ButtonGroupEnd,
  GrayTitle,
  LoadingGif,
  ModalBody,
  ModalBodyContainer,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  StyledHR,
  TextError,
  TitleAndButton,
} from "../../../shared/styles/global.styles";
import loadSCGif from "../../../assets/load_SC.gif";
import {
  Table,
  TableContainer,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/Table/styles";
import Button from "../../../components/Button/button";
import { Label } from "../../../components/Label/styles";
import SelectComponent from "../../../components/Select/select";
import { DatePickerInputContainer } from "../../../components/Input/styles";
import { Lotacao } from "../../../shared/interfaces/DadosFinanceirosInterface";
import lotacaoService from "../../../services/LotacaoService";
import RelatorioService from "../../../services/RelatorioService";
import { LoadingContainer } from "../../Remessa/ListarRemessas/styles";

export interface MovimentacoesData {
  TITULAR: MovimentacaoCategoria;
  DEPENDENTE: MovimentacaoCategoria;
}

export interface MovimentacaoCategoria {
  ANTERIOR: number;
  INCLUSAO: number;
  REATIVACAO: number;
  EXCLUSAO: number;
  TOTAL: number;
}

const RelatorioMovimentacoes: React.FC = () => {
  const [lotacoes, setLotacoes] = useState<Lotacao[]>([]);
  const [selectLotacao, setSelectLotacao] = useState<Lotacao>();
  const [appliedLotacao, setAppliedLotacao] = useState<Lotacao>();
  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined]);
  const [appliedDateRange, setAppliedDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined]);
  const [showModalReport, setShowModalReport] = useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isRequest, setIsRequest] = useState<boolean>(false);
  const [relatorioMovimentacoes, setRelatorioMovimentacoes] =
    useState<MovimentacoesData>({} as MovimentacoesData);
  const [PDF, setPDF] = useState<Blob>();

  const clearError = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    setInvalidFields((prev) => prev.filter((field) => field !== name));
  };

  const fetchLotacoes = async () => {
    try {
      const response = await lotacaoService.getAllLotacoes();
      setLotacoes(response);
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar lotações. Por favor, tente novamente.",
        icon: "error",
        confirmButtonText: "Fechar",
        allowOutsideClick: false,
      });
    }
  };

  const generateReport = async () => {
    setIsRequest(true);

    const newInvalidFields: string[] = [];

    if ((!dateRange[0] && !dateRange[1]) || !dateRange[0] || !dateRange[1]) {
      newInvalidFields.push("dataMovimentacaoCadastral");
    } else {
      if (dateRange[1] < dateRange[0]) {
        newInvalidFields.push("dataMovimentacaoCadastral");
      }
    }

    if (newInvalidFields.length > 0) {
      setInvalidFields(newInvalidFields);

      Swal.fire({
        icon: "error",
        title: "Erro nos campos",
        text: "Por favor, preencha todos os campos obrigatórios.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });

      setIsRequest(false);
      return;
    }

    const confirmAlert = await Swal.fire({
      title: "Confirmar",
      text: "Você tem certeza que deseja gerar o relatório de movimentações dos contribuintes?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
      allowOutsideClick: false,
    });

    if (confirmAlert.isConfirmed) {
      Swal.fire({
        title: "Gerando relatório...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      let relatorioData: MovimentacoesData | undefined = undefined;
      if (dateRange[0] && dateRange[1]) {
        await RelatorioService.getMovimentacaoReport(
          dateRange[0],
          dateRange[1],
          selectLotacao?.id?.toString() ?? undefined
        )
          .then((response) => {
            relatorioData = response;
          })
          .catch((error) => {
            console.error(error);
            Swal.fire({
              icon: "error",
              title: "Erro ao buscar resumo de movimentações",
              text:
                error.response?.data?.message ||
                "Erro ao buscar resumo de movimentações",
              allowOutsideClick: false,
            });
          });
      } else {
        throw new Error("Datas inválidas para gerar o relatório.");
      }

      if (relatorioData) {
        setRelatorioMovimentacoes(relatorioData);
        setAppliedLotacao(selectLotacao);
        setAppliedDateRange(dateRange);

        Swal.fire({
          icon: "success",
          title: "Sucesso",
          text: "Relatório gerado com sucesso!",
          showCancelButton: true,
          confirmButtonText: "Imprimir",
          cancelButtonText: "Fechar",
          allowOutsideClick: false,
        }).then((willPrint) => {
          if (willPrint.isConfirmed) {
            imprimirRelatorio(relatorioData!, selectLotacao, dateRange);
          }
        });
      }
    }
    closeReportModal();
  };

  const handleDateRangeChange = (
    dates: [Date | undefined, Date | undefined]
  ) => {
    // Convertendo valores null para undefined
    const [start, end] = dates;

    if (start) {
      setDateRange([start, dateRange[1]]);
    }
    if (end) {
      setDateRange([dateRange[0], end]);
    }
  };

  const closeReportModal = () => {
    setShowModalReport(false);
    setSelectLotacao(undefined);
    setDateRange([undefined, undefined]);
    setInvalidFields([]);
    setIsRequest(false);
  };

  // Função para formatar a entrada manual de datas
  const handleDateInputChange = (e: any) => {
    if (!e || !e.target || typeof e.target.value !== "string") return;

    let value = e.target.value;

    // Não fazer nada se o campo estiver vazio
    if (!value) return;

    // Verificar se o valor contém hífen (separando intervalo de datas)
    const parts = value.split("-");

    if (parts.length > 1) {
      // Processar intervalo de datas (início - fim)
      let startDate = parts[0].trim();
      let endDate = parts[1].trim();

      // Formatar a primeira data
      startDate = formatDateInput(startDate);

      // Formatar a segunda data
      endDate = formatDateInput(endDate);

      // Recompor o valor com o formato correto
      e.target.value = `${startDate} - ${endDate}`;
    } else {
      // Processar uma única data
      const formattedDate = formatDateInput(value);

      // Verificar se a primeira data está completa (10 caracteres = dd/mm/aaaa)
      // Se estiver completa e não for uma operação de apagar (verificar se o valor atual é maior que o anterior)
      if (formattedDate.length === 10 && value.length >= formattedDate.length) {
        // Adicionar o hífen automaticamente
        e.target.value = `${formattedDate} - `;
      } else {
        e.target.value = formattedDate;
      }
    }
  };

  // Função auxiliar para formatar uma data individual
  const formatDateInput = (value: string): string => {
    // Remover caracteres não numéricos
    let numbers = value.replace(/\D/g, "");

    // Limitar a 8 dígitos (ddmmyyyy)
    numbers = numbers.substring(0, 8);

    // Aplicar máscara dd/mm/yyyy
    let formattedValue = "";
    if (numbers.length > 0) {
      formattedValue = numbers.substring(0, Math.min(2, numbers.length));

      if (numbers.length > 2) {
        formattedValue +=
          "/" + numbers.substring(2, Math.min(4, numbers.length));
      }

      if (numbers.length > 4) {
        formattedValue +=
          "/" + numbers.substring(4, Math.min(8, numbers.length));
      }
    }

    return formattedValue;
  };

  const imprimirRelatorio = async (
    data: MovimentacoesData,
    lotacao?: Lotacao,
    dateRange?: [Date | undefined, Date | undefined]
  ) => {
    Swal.fire({
      title: "Gerando PDF do relatório...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (PDF) {
      const url = URL.createObjectURL(PDF);
      window.open(url);
      Swal.close();
      return;
    }

    await pdf(
      <RelatorioPDF
        data={data}
        lotacao={lotacao?.descricao}
        dateRange={{
          inicio: dateRange![0]?.toLocaleDateString("pt-BR") || "",
          fim: dateRange![1]?.toLocaleDateString("pt-BR") || "",
        }}
      />
    )
      .toBlob()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url);
        setPDF(blob);
      })
      .finally(() => {
        Swal.close();
        closeReportModal();
      });
  };

  const clearReport = () => {
    setAppliedLotacao(undefined);
    setAppliedDateRange([undefined, undefined]);
    setShowModalReport(false);
    setIsRequest(false);
    setInvalidFields([]);
    setPDF(undefined);
    setRelatorioMovimentacoes({} as MovimentacoesData);
  };

  useEffect(() => {
    fetchLotacoes();
  }, []);

  return (
    <>
      <Container>
        <TitleAndButton>
          <GrayTitle>Relatório de Movimentações dos Contribuintes</GrayTitle>
          <ContainerButton>
            {Object.keys(relatorioMovimentacoes).length > 0 && (
              <Button
                schemaButton="out-primary"
                action={() =>
                  imprimirRelatorio(
                    relatorioMovimentacoes,
                    appliedLotacao,
                    appliedDateRange
                  )
                }
                disabled={isRequest}
              >
                Imprimir
              </Button>
            )}
            {appliedDateRange[0] && appliedDateRange[1] && (
              <Button
                schemaButton="full-primary"
                action={() => {
                  clearReport();
                }}
              >
                Limpar dados do relatório
              </Button>
            )}
            {!appliedDateRange[0] && !appliedDateRange[1] && (
              <Button
                schemaButton="full-primary"
                action={() => setShowModalReport(true)}
                disabled={isRequest}
              >
                Gerar Relatório
              </Button>
            )}
          </ContainerButton>
        </TitleAndButton>

        <StyledHR />

        {(appliedLotacao || (appliedDateRange[0] && appliedDateRange[1])) && (
          <AppliedFiltersWrapper>
            <FilterTitle>Dados do relatório:</FilterTitle>
            <FiltersContainer>
              {appliedLotacao && (
                <FilterItem>
                  <FilterKey>Lotação: </FilterKey>
                  <FilterValue>{appliedLotacao.descricao}</FilterValue>
                </FilterItem>
              )}

              {appliedDateRange[0] && appliedDateRange[1] && (
                <FilterItem>
                  <FilterKey>Período: </FilterKey>
                  <FilterValue>
                    {appliedDateRange[0].toLocaleDateString("pt-BR")} -{" "}
                    {appliedDateRange[1].toLocaleDateString("pt-BR")}
                  </FilterValue>
                </FilterItem>
              )}
            </FiltersContainer>
          </AppliedFiltersWrapper>
        )}

        <TableContainer>
          {isRequest ? (
            <LoadingContainer>
              <LoadingGif src={loadSCGif} alt="Carregando..." />
            </LoadingContainer>
          ) : (
            <Table
              id="relatorio-movimentacoes-tabela"
              style={{ minHeight: "175px" }}
            >
              <TableHead>
                <TableRow>
                  <TableHeader></TableHeader>
                  <TableHeader>Anterior</TableHeader>
                  <TableHeader>Inclusão</TableHeader>
                  <TableHeader>Reativação</TableHeader>
                  <TableHeader>Exclusão</TableHeader>
                  <TableHeader>Total</TableHeader>
                </TableRow>
              </TableHead>
              <tbody>
                {Object.keys(relatorioMovimentacoes).length > 0 ? (
                  <>
                    {Object.entries(relatorioMovimentacoes).map(
                      ([key, value]) => (
                        <TableRow key={key}>
                          <TableData>{key}</TableData>
                          <TableData>{value.ANTERIOR}</TableData>
                          <TableData>{value.INCLUSAO}</TableData>
                          <TableData>{value.REATIVACAO}</TableData>
                          <TableData>{value.EXCLUSAO}</TableData>
                          <TableData>{value.TOTAL}</TableData>
                        </TableRow>
                      )
                    )}

                    <TableRow>
                      <TableData>TOTAL GERAL</TableData>
                      <TableData>
                        {Object.values(relatorioMovimentacoes).reduce(
                          (acc, curr) => acc + curr.ANTERIOR,
                          0
                        )}
                      </TableData>
                      <TableData>
                        {Object.values(relatorioMovimentacoes).reduce(
                          (acc, curr) => acc + curr.INCLUSAO,
                          0
                        )}
                      </TableData>
                      <TableData>
                        {Object.values(relatorioMovimentacoes).reduce(
                          (acc, curr) => acc + curr.REATIVACAO,
                          0
                        )}
                      </TableData>
                      <TableData>
                        {Object.values(relatorioMovimentacoes).reduce(
                          (acc, curr) => acc + curr.EXCLUSAO,
                          0
                        )}
                      </TableData>
                      <TableData>
                        {Object.values(relatorioMovimentacoes).reduce(
                          (acc, curr) => acc + curr.TOTAL,
                          0
                        )}
                      </TableData>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableData colSpan={6} className="text-center text-muted">
                      {"Sem resultados"}
                    </TableData>
                  </TableRow>
                )}
              </tbody>
            </Table>
          )}
        </TableContainer>

        <ModalContainer show={showModalReport}>
          <ModalContent style={{ marginTop: "-12%" }} width={35}>
            <Button
              type="button"
              schemaButton="x-close"
              action={() => closeReportModal()}
              icon={<IoCloseSharp size={30} />}
              position="absolute"
              top={10}
              right={10}
            />
            <ModalHeader>
              <BlueTitle>Gerar relatório</BlueTitle>
            </ModalHeader>

            <ModalBody>
              <Col>
                <Label htmlFor="lotacao">Lotação</Label>
                <SelectComponent
                  color="black"
                  placeholder="Todas Lotações"
                  isPlaceHolder={false}
                  id="lotacao"
                  width={Number("100%")}
                  name="lotacao"
                  disabled={isRequest}
                  value={selectLotacao?.id?.toString() ?? ""}
                  onChange={(e) => {
                    if (e.target.value === "0") {
                      setSelectLotacao({ id: 0, descricao: "Sem lotação" });
                    } else {
                      const selectedLotacao = lotacoes.find(
                        (lotacao) => lotacao.id === parseInt(e.target.value)
                      );
                      setSelectLotacao(selectedLotacao);
                    }
                    clearError(e);
                  }}
                  options={[
                    { value: "0", label: "Sem lotação" },
                    ...lotacoes.map((lotacao) => ({
                      value: lotacao.id!.toString(),
                      label: lotacao.descricao,
                    })),
                  ]}
                />
              </Col>
              <Col>
                <Label
                  className="filtro-label"
                  htmlFor="dataMovimentacaoCadastral"
                >
                  Data da movimentação cadastral <TextError>*</TextError>
                </Label>
                <DatePickerInputContainer
                  width={Number("100%")}
                  hasIconLeft={false}
                  hasIconRight={false}
                  error={
                    invalidFields.includes("dataMovimentacaoCadastral")
                      ? true
                      : false
                  }
                  className={
                    dateRange[0] || dateRange[1] ? "has-date-content" : ""
                  }
                >
                  <DatePicker
                    selectsRange={true}
                    name="dataMovimentacaoCadastral"
                    startDate={dateRange[0] as Date | undefined}
                    endDate={dateRange[1] as Date | undefined}
                    onChange={(dates) => {
                      handleDateRangeChange(
                        dates as [Date | undefined, Date | undefined]
                      );

                      setInvalidFields((prev) =>
                        prev.filter(
                          (field) => field !== "dataMovimentacaoCadastral"
                        )
                      );
                    }}
                    onChangeRaw={handleDateInputChange}
                    calendarStartDay={0}
                    className={`date-picker-input placeholder-custom ${
                      dateRange[0] || dateRange[1] ? "has-value" : "no-value"
                    }`}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="00/00/0000 - 00/00/0000"
                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                    isClearable={false}
                    showYearDropdown
                    showMonthDropdown
                    scrollableMonthYearDropdown
                    wrapperClassName={
                      dateRange[0] || dateRange[1]
                        ? "date-wrapper-filled"
                        : "date-wrapper-empty"
                    }
                    locale="pt-BR"
                    autoComplete="off"
                    allowSameDay
                    onKeyDown={(e) => {
                      if (e.key === "Delete" || e.key === "Backspace") {
                        setDateRange([undefined, undefined]);
                        return;
                      }
                    }}
                  />
                </DatePickerInputContainer>
                {invalidFields.includes("dataMovimentacaoCadastral") && (
                  <TextError>
                    Preencha a data de vencimento corretamente
                  </TextError>
                )}
              </Col>
            </ModalBody>
            <ModalFooter>
              <Button
                schemaButton="full-primary"
                action={() => {
                  generateReport();
                }}
                disabled={isRequest}
              >
                Gerar Relatório
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalContainer>
      </Container>
    </>
  );
};

export default RelatorioMovimentacoes;
